// Angular imports
import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router, NavigationStart, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

// Translation service import
import { TranslateService } from '@ngx-translate/core';

// Service imports
import { ApplicationService } from 'src/app/services/application.service';
import { ArticleService } from 'src/app/services/article.service';

import {
  AnimationMetadata,
  trigger,
  transition,
  style,
  animate,
  query,
  stagger,
} from '@angular/animations';
import { AccountService } from 'src/app/services/account.service';
import { saveAs } from 'file-saver';

const pageAnimationsDefs: AnimationMetadata[] = [
  transition(':enter', [
    query('.model-container', [
      style({ opacity: 0, transform: 'scale(0.8)' }),
      stagger(30, [
        animate('400ms cubic-bezier(0.35, 0, 0.25, 1)', style({ opacity: 1, transform: 'scale(1)' })),
      ]),
    ], { optional: true }),
  ]),
];

@Component({
    selector: 'app-lineup-page',
    templateUrl: './lineup-page.component.html',
    styleUrls: ['./lineup-page.component.scss'],
    animations: [
        // eslint-disable-next-line deprecation/deprecation
        // @ts-ignore: trigger() is deprecated in Angular 21 but has no non-breaking replacement for template-bound animations
        trigger('pageAnimations', pageAnimationsDefs),
    ],
    standalone: false
})
export class LineupPageComponent implements OnInit, OnDestroy, AfterViewInit {
  
  // Route parameters
  companyGroupCode: string = '';
  year: string = '';
  
  // Product data grouped by family
  productData: { [familyName: string]: any[] } = {};
  
  // Track selected color variant for each model
  selectedVariants: { [modelId: string]: number } = {};
  
  // Brand dropdown
  brandOptions: { name: string; logo: string }[] = [];
  selectedBrand: string = '';

  // Family filter dropdown
  familyOptions: { label: string; value: string }[] = [];
  selectedFamily: string = '';

  // Model year filter dropdown
  modelYearCounts: { [year: string]: number } = {};
  modelYearOptions: string[] = [];
  selectedModelYear: string = '';
  
  // Loading states
  isLoading: boolean = true;
  hasError: boolean = false;
  errorMessage: string = '';

  isDetailView: boolean = false;
  isFirstVisit: boolean = true;
  isDownloading: boolean = false;
  filterBarHeight: number = 0;
  navbarFixed: boolean = localStorage.getItem('navbar-not-fixed') !== '1';

  private savedScrollY = 0;
  private routerSub: Subscription;
  private filterBarObserver: ResizeObserver;

  @ViewChild('filterBarRef') filterBarRef: ElementRef<HTMLElement>;
  isInternal: boolean;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private articleService: ArticleService,
    private applicationService: ApplicationService,
    private translateService: TranslateService,
    private accountService: AccountService
  ) {}

  ngOnInit(): void {
    // Initialise detail-view state in case we land directly on a child URL
    this.isDetailView = !!this.route.firstChild;

    this.isInternal = this.accountService.isInternal() || this.accountService.isHelpdesk();

    // Save scroll position before navigating away; restore when returning
    this.routerSub = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        // Only save scroll when the grid is visible (not from inside a detail view)
        if (!this.isDetailView) {
          this.savedScrollY = window.scrollY;
        }
      }
      if (event instanceof NavigationEnd) {
        this.isDetailView = !!this.route.firstChild;
        // Returning to grid from detail page — restore scroll, disable animation
        if (!this.isDetailView && this.savedScrollY > 0) {
          this.isFirstVisit = false;
          setTimeout(() => window.scrollTo({ top: this.savedScrollY, behavior: 'instant' as ScrollBehavior }), 0);
        }
      }
    });

    // Get route parameters
    this.route.params.subscribe(params => {
      this.companyGroupCode = params['cgc'] || '';
      this.year = params['year'] || '';

      // Load available brands for this company group
      this.brandOptions = (this.applicationService.getSelectedCompanyGroupBrands(this.companyGroupCode) || [])
        .filter((b: any) => b.show !== false);
      if (this.brandOptions.length > 0) {
        this.selectedBrand = this.brandOptions[0].name;
      }

      // Load lineup data
      this.loadLineupData();
    });

    this.route.queryParamMap.subscribe((queryParams) => {
      const modelYearFromUrl = (queryParams.get('modelyear') || '').trim();

      if (modelYearFromUrl === this.selectedModelYear) {
        return;
      }

      this.selectedFamily = '';

      if (this.selectedBrand) {
        this.selectedVariants = {};
      }
    });
  }

  ngAfterViewInit(): void {
    const fixedBar = this.filterBarRef?.nativeElement?.querySelector('.filter-bar-fixed') as HTMLElement;
    if (fixedBar) {
      this.filterBarHeight = fixedBar.offsetHeight;
      this.filterBarObserver = new ResizeObserver(() => {
        this.filterBarHeight = fixedBar.offsetHeight;
      });
      this.filterBarObserver.observe(fixedBar);
    }
    // Handle hash-based scrolling after view renders
    setTimeout(() => {
      this.handleHashScroll();
    }, 100);
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    this.filterBarObserver?.disconnect();
  }

  /**
   * Load lineup data from article service
   */
  loadLineupData(): void {
    this.isLoading = true;
    this.hasError = false;
  
    this.articleService.getLineupProducts(this.selectedBrand, this.selectedModelYear).subscribe({
      next: (response: any) => {
        this.isLoading = false;
 
        if (response && response.result) {
          this.productData = response.result;
          this.modelYearCounts = response.modelYears || {};

          this.buildModelYearOptions();
          // Build family options for dropdown
          this.buildFamilyOptions();
          this.syncModelYearToUrl();
    
          setTimeout(() => {  
            window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
          }, 300);


        } else {
          this.hasError = true;
          this.errorMessage = 'No data available';
        }
      },
      error: (error) => {
        console.error('❌ Error loading lineup data:', error);
        this.isLoading = false;
        this.hasError = true;
        this.errorMessage = 'Failed to load lineup data';
      }
    });
  }

  downloadLineup(): void {
    this.isDownloading = true;
    const excelName = this.selectedModelYear
      ? `lineup-${this.selectedBrand}-${this.selectedModelYear}.xlsx`
      : `lineup-${this.selectedBrand}.xlsx`;

    this.articleService.downloadLineupProducts(this.selectedBrand, this.selectedModelYear).subscribe({
      next: (blob: any) => {
        this.applicationService.hideLoader();
        const file = new Blob([blob], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        saveAs(file, excelName);
        this.isDownloading = false;
      },
      error: () => {
        this.applicationService.hideLoader();
        this.isDownloading = false;
      },
    });
  }

  /**
   * Select a color variant for a model
   */
  selectVariant(modelId: string, variantIndex: number, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.selectedVariants[modelId] = variantIndex;
  }

  /**
   * Handle hash-based scrolling to specific sections
   */
  handleHashScroll(): void {
    if (window.location.hash) {
      const sectionId = window.location.hash.substring(1); // Remove the # symbol
      const element = document.getElementById(sectionId);
      
      if (element) {
        const headerHeight = 100; // Approximate header height
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - headerHeight - 20;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  }

  /**
   * Get family names for iteration
   */
  getFamilyNames(): string[] {
    return Object.keys(this.getFilteredProductData()).sort((a, b) => a.localeCompare(b));
  }

  getModelsByFamily(familyName: string): any[] {
    return this.getFilteredProductData()[familyName] || [];
  }

  /**
   * Generate section ID for a family
   */
  getSectionId(familyName: string): string {
    return 'section-' + familyName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  }

  /**
   * Check if a variant is selected
   */
  isVariantSelected(modelId: string, variantIndex: number): boolean {
    return this.selectedVariants[modelId] === variantIndex;
  }

  /**
   * Get the active variant for a model (selected, or first by default)
   */
  getActiveVariant(model: any): any {
    const idx = this.selectedVariants[model.uiKey];
    return model.variants?.[idx ?? 0] ?? null;
  }

  /**
   * Build family options for dropdown
   */
  buildFamilyOptions(): void {
    const options = this.getFamilyNames().map(familyName => ({
      label: familyName,
      value: this.getSectionId(familyName),
    }));

    this.familyOptions = [
      { label: this.translateService.instant('ALL') || 'All Families', value: '' },
      ...options
    ];
  }

  buildModelYearOptions(): void {
    const yearsFromApi = Object.keys(this.modelYearCounts || {});
    let years = yearsFromApi;

    if (!years.length) {
      const yearSet = new Set<string>();
      Object.values(this.productData).forEach((models: any[]) => {
        models?.forEach((model) => {
          (model?.years || []).forEach((year: string) => yearSet.add(String(year)));
        });
      });
      years = Array.from(yearSet);
    }

    this.modelYearOptions = years.sort((a, b) => Number(b) - Number(a));
  }

  private getFilteredProductData(): { [familyName: string]: any[] } {
    if (!this.selectedModelYear) {
      return this.productData;
    }

    const filteredData: { [familyName: string]: any[] } = {};

    Object.entries(this.productData).forEach(([familyName, models]) => {
      const filteredModels = (models || []).filter((model: any) => this.modelMatchesYear(model));
      if (filteredModels.length) {
        filteredData[familyName] = filteredModels;
      }
    });

    return filteredData;
  }

  private modelMatchesYear(model: any): boolean {
    if (!this.selectedModelYear) {
      return true;
    }

    const years = (model?.years || []).map((year: any) => String(year));
    return years.includes(this.selectedModelYear);
  }

  /**
   * Handle brand selection change
   */
  onBrandChange(brandName: string): void {
    this.selectedBrand = brandName;
    this.selectedFamily = '';
    this.selectedModelYear = '';
    this.selectedVariants = {};
    this.loadLineupData();
  }

  onModelYearChange(): void {
    this.selectedFamily = '';
    this.syncModelYearToUrl();
  }

  private syncModelYearToUrl(): void {
    const currentQueryYear = (this.route.snapshot.queryParamMap.get('modelyear') || '').trim();
    const selectedYear = (this.selectedModelYear || '').trim();

    if (currentQueryYear === selectedYear) {
      return;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { modelyear: selectedYear || null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });

    this.loadLineupData();
  }

  /**
   * Handle family selection change
   */
  onFamilyChange(event: any): void {
    // If a specific family is selected, scroll to it
    if (this.selectedFamily && this.selectedFamily.trim() !== '') {
      const sectionId = this.selectedFamily;
      const element = document.getElementById(sectionId);
      
      if (element) {
        const headerHeight = 100; // Approximate header height
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - headerHeight - 20;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    } else {
      // Scroll to top when "All" is selected
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }

}
